import 'tippy.js/dist/tippy.css';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import type { Value } from 'platejs';
import type { TPlateEditor } from 'platejs/react';
import { Plate, PlateContent, usePlateEditor } from 'platejs/react';

import { createEditorPlugins } from './plugins/createEditorPlugins';
import { options } from './options';
import { EditorToolbar, IEditorToolbarProps } from './components/EditorToolbar';
import { EditorBlockMenu } from './components/EditorBlockMenu';
import { createEmptyLeafRenderer } from './plugins/empty-leaf';

const emptyValue: Value = [
    {
        type: options.p.type,
        children: [{ text: '' }],
    },
];

const isEqualContent = (a: Value, b: Value) =>
    JSON.stringify(a) === JSON.stringify(b);

interface IProps {
    content?: Value;
    readOnly?: boolean;
    role?: string;
    toolbarProps?: IEditorToolbarProps;
    onChange?(value: Value): void;
}

export const ContentEditor = ({
    content,
    readOnly,
    onChange,
    role,
    toolbarProps,
}: IProps) => {
    const pendingContent = useRef(content);
    pendingContent.current = content;

    const isEditorReady = useRef(false);
    const pushedProp = useRef<Value | undefined>(undefined);
    const pushedResult = useRef<Value | undefined>(undefined);

    const plugins = useMemo(() => createEditorPlugins(), []);

    const applyContent = useCallback(
        (plateEditor: TPlateEditor, value: Value) => {
            plateEditor.tf.setValue(value);
            pushedProp.current = value;
            pushedResult.current = plateEditor.children as Value;
        },
        []
    );

    const editor = usePlateEditor({
        plugins,
        value: emptyValue,
        onReady: ({ editor: plateEditor }) => {
            isEditorReady.current = true;

            const next = pendingContent.current;

            if (!next) {
                // No content was ever pushed (new/empty article), so the
                // editor keeps its initial emptyValue. Seed the baseline
                // anyway so the dedup guard in handleChange has something
                // to compare against — otherwise the very first onChange
                // (which Plate also fires on a pure selection/click, not
                // just an edit) slips through as a false "real edit".
                pushedResult.current = plateEditor.children as Value;

                return;
            }

            applyContent(plateEditor, next);
        },
    });

    useEffect(() => {
        if (!editor || !content) return;

        if (
            pushedProp.current !== undefined &&
            isEqualContent(content, pushedProp.current)
        ) {
            return;
        }

        applyContent(editor, content);
    }, [editor, content, applyContent]);

    const handleChange = useCallback(
        ({ value }: { value: Value }) => {
            if (!isEditorReady.current || !onChange) return;

            // Plate's onChange also fires on selection-only changes (no
            // content edit). Compare against the last known value and keep
            // refreshing that baseline on every real edit, so a pure cursor
            // move never re-fires onChange — not just the first one.
            if (
                pushedResult.current !== undefined &&
                isEqualContent(pushedResult.current, value)
            ) {
                return;
            }

            pushedResult.current = value;

            // The parent is expected to feed this value straight back in as
            // `content`. Record it as already-applied so that round trip is a
            // no-op for the effect above, instead of re-running setValue and
            // resetting the selection on every keystroke.
            pushedProp.current = value;

            onChange(value);
        },
        [onChange]
    );

    const renderLeaf = useMemo(
        () => (editor ? createEmptyLeafRenderer(editor, readOnly) : undefined),
        [editor, readOnly]
    );

    return (
        <Plate editor={editor} onChange={handleChange}>
            {!readOnly && (
                <>
                    <EditorToolbar {...toolbarProps} />
                    <EditorBlockMenu />
                </>
            )}
            <Box
                sx={{
                    '& [data-slate-editor]:focus-visible': {
                        outline: 'none',
                        boxShadow: (theme) =>
                            `inset 0 0 0 1px ${theme.vars.palette.grey[300]}`,
                    },
                }}
            >
                <PlateContent
                    readOnly={readOnly}
                    renderLeaf={renderLeaf}
                    role={role}
                />
            </Box>
        </Plate>
    );
};
